import { ChatMessage } from '@/hooks/useAIChat';
import { cn } from '@/lib/utils';
import { escapeHtml } from '@/lib/htmlEscape';
import { User, Sparkles } from 'lucide-react';

interface AIChatMessageProps {
  message: ChatMessage;
}

export function AIChatMessage({ message }: AIChatMessageProps) {
  const isUser = message.role === 'user';

  // Simple markdown-like formatting for assistant messages
  const formatContent = (content: string) => {
    if (isUser) return content;

    // Escape first so customer names (or other DB data) cannot inject HTML
    let formatted = escapeHtml(content);
    // Convert **bold** to <strong>
    formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // Convert *italic* to <em>
    formatted = formatted.replace(/\*(.+?)\*/g, '<em>$1</em>');
    // Convert bullet points
    formatted = formatted.replace(/^- (.+)$/gm, '• $1');
    // Convert numbered lists
    formatted = formatted.replace(/^\d+\. (.+)$/gm, '<span class="ml-4">$&</span>');
    // Convert newlines to <br>
    formatted = formatted.replace(/\n/g, '<br>');

    return formatted;
  };

  return (
    <div
      className={cn(
        "flex gap-3",
        isUser ? "flex-row-reverse" : "flex-row"
      )}
    >
      <div
        className={cn(
          "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
          isUser ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"
        )}
      >
        {isUser ? (
          <User className="h-4 w-4" />
        ) : (
          <Sparkles className="h-4 w-4" />
        )}
      </div>
      <div
        className={cn(
          "max-w-[80%] rounded-lg px-4 py-2 text-sm",
          isUser
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-foreground"
        )}
      >
        {isUser ? (
          <p>{message.content}</p>
        ) : (
          <div
            className="prose prose-sm dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: formatContent(message.content) }}
          />
        )}
      </div>
    </div>
  );
}
